/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  ConditionExpression,
  ConditionExpression$inboundSchema,
  ConditionExpression$Outbound,
  ConditionExpression$outboundSchema,
} from "./conditionexpression.js";
import {
  IconConfig,
  IconConfig$inboundSchema,
  IconConfig$Outbound,
  IconConfig$outboundSchema,
} from "./iconconfig.js";
import {
  IconRefMap,
  IconRefMap$inboundSchema,
  IconRefMap$Outbound,
  IconRefMap$outboundSchema,
} from "./iconrefmap.js";

/**
 * A single state for the icon.
 */
export type IconStateConfig = {
  /**
   * Defines a condition expression. Can be a single condition, a group of conditions, or nested groups. Exactly one of 'condition', 'condition_group', or 'nested_expression' must be specified.
   */
  conditionExpression?: ConditionExpression | undefined;
  /**
   * Specifies configuration for an icon.
   */
  config?: IconConfig | undefined;
  /**
   * A mapping of icon names and variants to a enum values for a ref.
   */
  iconRefMap?: IconRefMap | undefined;
  /**
   * Whether this state is the default state. If this is true, conditions are not required. For readability, these states should be at the end of the array.
   */
  isDefault?: boolean | undefined;
  /**
   * The priority of the state. There is no constraint on overlapping conditions, so this field allows user to set priority.
   */
  priority?: number | undefined;
  /**
   * The name of the state
   */
  stateName: string;
};

/** @internal */
export const IconStateConfig$inboundSchema: z.ZodType<
  IconStateConfig,
  z.ZodTypeDef,
  unknown
> = z.object({
  condition_expression: types.optional(ConditionExpression$inboundSchema),
  config: types.optional(IconConfig$inboundSchema),
  icon_ref_map: types.optional(IconRefMap$inboundSchema),
  is_default: types.boolean().default(false),
  priority: types.number().default(0),
  state_name: types.string(),
}).transform((v) => {
  return remap$(v, {
    "condition_expression": "conditionExpression",
    "icon_ref_map": "iconRefMap",
    "is_default": "isDefault",
    "state_name": "stateName",
  });
});
/** @internal */
export type IconStateConfig$Outbound = {
  condition_expression?: ConditionExpression$Outbound | undefined;
  config?: IconConfig$Outbound | undefined;
  icon_ref_map?: IconRefMap$Outbound | undefined;
  is_default: boolean;
  priority: number;
  state_name: string;
};

/** @internal */
export const IconStateConfig$outboundSchema: z.ZodType<
  IconStateConfig$Outbound,
  z.ZodTypeDef,
  IconStateConfig
> = z.object({
  conditionExpression: ConditionExpression$outboundSchema.optional(),
  config: IconConfig$outboundSchema.optional(),
  iconRefMap: IconRefMap$outboundSchema.optional(),
  isDefault: z.boolean().default(false),
  priority: z.number().int().default(0),
  stateName: z.string(),
}).transform((v) => {
  return remap$(v, {
    conditionExpression: "condition_expression",
    iconRefMap: "icon_ref_map",
    isDefault: "is_default",
    stateName: "state_name",
  });
});

export function iconStateConfigToJSON(
  iconStateConfig: IconStateConfig,
): string {
  return JSON.stringify(IconStateConfig$outboundSchema.parse(iconStateConfig));
}
export function iconStateConfigFromJSON(
  jsonString: string,
): SafeParseResult<IconStateConfig, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => IconStateConfig$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'IconStateConfig' from JSON`,
  );
}
